import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  House, Gauge, GameController, ArrowRight, Syringe,
  Flask, Bandaids, Drop, Pill, Trash,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Premium "waste stream misplaced" 404 page.
 * - Top-left rescigo logo (link to /).
 * - Centerpiece: "4 [3D rotating biohazard] 4" — the biohazard *is* the zero.
 * - Particles, glow, floating biomedical icons.
 * - 3 CTAs: Home / Dashboard / Play Segregation Challenge.
 */
export default function NotFound() {
  useEffect(() => {
    document.title = "Page not found · Rescigo Health";
    // Iter 237 — fire a security beacon so probe attempts on suspicious
    // paths (e.g. /wp-admin, /.env, /phpmyadmin) get logged. Server-side
    // decides whether the path is suspicious; legit typos are ignored.
    try {
      fetch(`${BACKEND_URL}/api/security/probe-beacon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          path: window.location.pathname,
          referrer: document.referrer || "",
        }),
        keepalive: true,
      }).catch(() => { /* silent */ });
    } catch { /* ignore */ }
  }, []);

  // Floating waste icons (deterministic positions, framer animations)
  const float = useMemo(() => [
    { Icon: Syringe,    x: "8%",   y: "22%",  d: 0.0, size: 28, hue: "#22d3a8" },
    { Icon: Flask,      x: "12%",  y: "72%",  d: 0.6, size: 32, hue: "#fbbf24" },
    { Icon: Bandaids,   x: "85%",  y: "18%",  d: 1.2, size: 24, hue: "#f87171" },
    { Icon: Drop,       x: "88%",  y: "68%",  d: 1.8, size: 22, hue: "#60a5fa" },
    { Icon: Pill,       x: "20%",  y: "15%",  d: 2.4, size: 22, hue: "#c084fc" },
    { Icon: Trash,      x: "78%",  y: "82%",  d: 3.0, size: 26, hue: "#34d399" },
  ], []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#04140d] text-white"
          data-testid="page-404">
      {/* Background grid + radial glow */}
      <div className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(34,211,168,0.18), transparent 45%)," +
                "radial-gradient(circle at 80% 70%, rgba(177,42,30,0.16), transparent 50%)," +
                "linear-gradient(to bottom right, #02110b 0%, #04140d 40%, #060c12 100%)",
            }}/>
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.10] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)," +
                "linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}/>
      {/* Top-left brand logo */}
      <Link to="/" data-testid="404-brand-home"
            className="absolute top-6 left-6 flex items-center gap-2 z-30 group">
        <div className="absolute -inset-2 rounded-xl bg-emerald-400/0 group-hover:bg-emerald-400/10 transition"/>
        <img src="/brand/rescigo-logo-white.png" alt="Rescigo Health"
              className="h-9 w-auto relative"/>
      </Link>
      {/* Top-right tiny status */}
      <div className="absolute top-6 right-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-300/70 z-30">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>
        Compliance · Online
      </div>

      {/* Floating waste icons */}
      {float.map((f, i) => (
        <motion.div key={i}
                     className="absolute z-10 pointer-events-none"
                     style={{ left: f.x, top: f.y, color: f.hue }}
                     initial={{ opacity: 0, y: 0 }}
                     animate={{ opacity: [0.0, 0.55, 0.55, 0.0],
                                y: [0, -16, 0, 16, 0] }}
                     transition={{ duration: 7 + i * 0.6, delay: f.d,
                                   repeat: Infinity, ease: "easeInOut" }}>
          <f.Icon size={f.size} weight="duotone"
                   style={{ filter: `drop-shadow(0 0 12px ${f.hue}88)` }}/>
        </motion.div>
      ))}

      {/* Particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <motion.div key={`p${i}`}
                     className="absolute w-[3px] h-[3px] rounded-full bg-emerald-300/60 z-10"
                     style={{
                       left: `${(i * 37) % 100}%`,
                       top: `${(i * 53) % 100}%`,
                     }}
                     animate={{ opacity: [0, 1, 0], y: [0, -40, -80] }}
                     transition={{ duration: 4 + (i % 5),
                                   delay: (i % 7) * 0.3,
                                   repeat: Infinity, ease: "easeOut" }}/>
      ))}

      {/* Centerpiece */}
      <main className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] uppercase tracking-[0.22em] font-semibold mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"/>
          Misplaced waste stream
        </div>

        {/* "4 [biohazard] 4" — biohazard rotates as the zero */}
        <div className="flex items-center justify-center gap-2 sm:gap-6 select-none"
              data-testid="404-glyph">
          <Digit>4</Digit>
          <Biohazard/>
          <Digit>4</Digit>
        </div>

        <h1 className="mt-12 font-heading text-3xl md:text-5xl text-center max-w-2xl leading-[1.1]">
          Oops. This waste stream is&nbsp;<span className="text-emerald-300">misplaced</span>.
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-300 text-center max-w-xl leading-relaxed">
          The page you're looking for may have been improperly segregated.
          Let's redirect it safely.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" data-testid="404-cta-home"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-400 text-[#04140d] hover:bg-emerald-300 transition text-sm font-semibold">
            <House size={15} weight="fill"/> Return Home
          </Link>
          <Link to="/dashboard" data-testid="404-cta-dashboard"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:border-white/40 hover:bg-white/5 transition text-sm font-medium">
            <Gauge size={15} weight="duotone"/> Go to Dashboard
          </Link>
          <Link to="/game" data-testid="404-cta-game"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-amber-300/90 to-emerald-300/90 text-[#04140d] hover:opacity-95 transition text-sm font-semibold">
            <GameController size={15} weight="fill"/> Play Segregation Challenge
            <ArrowRight size={13}/>
          </Link>
        </div>

        {/* Compliance hint */}
        <div className="mt-12 inline-flex items-center gap-2 text-[11px] text-slate-400">
          <span className="font-mono">BMW · Rules 2016</span>
          <span>·</span>
          <span>Yellow · Red · White · Blue</span>
        </div>
      </main>
    </div>
  );
}

/* ----------------------------------------------------------- */
function Digit({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="font-heading text-[140px] sm:text-[200px] md:text-[260px] leading-none font-light tracking-tight"
      style={{
        background: "linear-gradient(180deg, #d1fae5 0%, #34d399 60%, #047857 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        textShadow: "0 0 40px rgba(52,211,153,0.25)",
      }}>
      {children}
    </motion.div>
  );
}

function Biohazard() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, rotate: -180 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="relative">
      {/* Outer pulsing aura */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(circle, rgba(245,158,11,0.55) 0%, rgba(245,158,11,0.0) 70%)",
          filter: "blur(8px)",
        }}/>
      {/* Inner ring */}
      <div className="relative w-[120px] h-[120px] sm:w-[170px] sm:h-[170px] md:w-[210px] md:h-[210px] rounded-full border border-amber-300/40 grid place-items-center"
            style={{
              boxShadow:
                "0 0 60px rgba(245,158,11,0.35) inset, 0 0 80px rgba(245,158,11,0.30)",
              background:
                "radial-gradient(circle at 50% 50%, rgba(15,40,28,0.85) 0%, rgba(4,20,13,0.95) 70%)",
            }}>
        {/* Rotating biohazard SVG */}
        <motion.svg
          viewBox="0 0 200 200"
          className="w-[78%] h-[78%] drop-shadow-[0_0_18px_rgba(251,191,36,0.65)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
          <defs>
            <radialGradient id="bh-fill" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fde68a"/>
              <stop offset="60%" stopColor="#f59e0b"/>
              <stop offset="100%" stopColor="#b45309"/>
            </radialGradient>
          </defs>
          {/* Three ring petals — rotated 120° each */}
          <g transform="translate(100,100)" fill="url(#bh-fill)">
            {[0, 120, 240].map((rot) => (
              <g key={rot} transform={`rotate(${rot})`}>
                <circle cx="0" cy="-58" r="32"/>
                <path d="M -22,-46 Q 0,-12 22,-46 Z" fill="#04140d"/>
              </g>
            ))}
            {/* Outer triangle masks (the negative cuts) */}
            {[60, 180, 300].map((rot) => (
              <g key={rot} transform={`rotate(${rot})`}>
                <path d="M 0,-22 L 30,30 L -30,30 Z" fill="#04140d"/>
              </g>
            ))}
            {/* Center disc */}
            <circle cx="0" cy="0" r="22" fill="url(#bh-fill)"/>
            <circle cx="0" cy="0" r="10" fill="#04140d"/>
          </g>
        </motion.svg>
      </div>
    </motion.div>
  );
}
